import {
  useAdminTransactions,
  useAdminUserDetail,
  useExtendCabinetTrial,
  useSetCabinetGold,
  type AdminUserDetail,
  type Cabinet,
  type MotifDoctolib,
  type User,
} from '@fidalia/api';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  CardTitle,
  DataTable,
  Pagination,
  Spinner,
  Switch,
  TabPanel,
  Tabs,
  type TableColumn,
} from '@fidalia/ui';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  MdApartment,
  MdArrowBack,
  MdBadge,
  MdBlock,
  MdBusiness,
  MdCalendarMonth,
  MdGroups,
  MdHourglassBottom,
  MdLabel,
  MdLogin,
  MdMail,
  MdMedicalServices,
  MdOpenInNew,
  MdOutgoingMail,
  MdPerson,
  MdPhone,
  MdPlace,
  MdReceiptLong,
  MdSync,
  MdWorkspacePremium,
} from 'react-icons/md';
import { useState, type ReactNode } from 'react';

import { colonnesTransactions, formatEuros } from '@/features/billing/transaction-columns';
import { ActiviteCabinet } from '@/features/emails/activite-cabinet';
import { TableauEnvois } from '@/features/emails/tableau-envois';
import { errorMessage } from '@/lib/auth';

const FONCTION_LABELS: Record<User['fonction'], string> = {
  praticien: 'Praticien',
  assistant: 'Assistant(e)',
  secretariat: 'Secrétariat',
  gestionnaire: 'Gestionnaire',
};

const EFFECTIF_LABELS = {
  '2-5': '2 à 5 praticiens',
  '6-10': '6 à 10 praticiens',
  '11+': '11 praticiens et plus',
} as const;

const STATUT_TONES: Record<Cabinet['statut'], 'brand' | 'success' | 'danger'> = {
  essai: 'brand',
  actif: 'success',
  impaye: 'danger',
  resilie: 'danger',
};

/** Essai dont l'échéance est passée, sans formule pour prendre le relais. */
function essaiEchu(cabinet: Cabinet): boolean {
  if (cabinet.statut !== 'essai' || cabinet.formule) return false;
  return cabinet.finEssai !== null && new Date(cabinet.finEssai).getTime() <= Date.now();
}

/** Les valeurs brutes de l'enum ne sont pas des libellés : ni accents, ni majuscules. */
const STATUT_LABELS: Record<Cabinet['statut'], string> = {
  essai: 'Essai gratuit',
  actif: 'Actif',
  impaye: 'Impayé',
  resilie: 'Résilié',
};

const ICONE_FORMULE = { solo: MdPerson, cabinet: MdGroups, centre: MdApartment } as const;

/** Même logique de priorité que le libellé : Gold, puis essai échu, puis formule ou essai en cours. */
function iconeAbonnement(cabinet: Cabinet, echu: boolean) {
  const Icone = (() => {
    if (cabinet.gold) return MdWorkspacePremium;
    if (echu) return MdBlock;
    if (!cabinet.formule) return MdHourglassBottom;
    return ICONE_FORMULE[cabinet.formule];
  })();

  return <Icone className="size-3.5" aria-hidden="true" />;
}

export const Route = createFileRoute('/_app/users/$id')({
  component: DetailPage,
});

function DetailPage() {
  const { id } = Route.useParams();
  const { data, isPending, error } = useAdminUserDetail(id);
  const [onglet, setOnglet] = useState<Onglet>('informations');

  if (isPending) {
    return (
      <div className="grid place-items-center py-20 text-content-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col gap-4">
        <Retour />
        <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert>
      </div>
    );
  }

  // `abonnements` du détail reste vide : l'historique vient de Stripe, via
  // `/admin/billing/transactions` scopé au cabinet (voir `Abonnements`).
  const { user, cabinet, accesSecondaires, doctolib } = data;
  const nom = `${user.prenom} ${user.nom}`.trim();
  const joursEssai = joursRestants(cabinet.finEssai);

  return (
    <div className="flex flex-col gap-6">
      <Retour />

      <div className="grid items-start gap-6 lg:grid-cols-[21rem_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-6">
          <Tabs
            id="fiche-utilisateur"
            aria-label="Sections de la fiche"
            value={onglet}
            onChange={setOnglet}
            items={[
              { value: 'informations', label: 'Informations', icon: <MdBadge /> },
              { value: 'abonnements', label: 'Abonnements', icon: <MdReceiptLong /> },
              {
                value: 'acces',
                label: 'Accès secondaires',
                icon: <MdGroups />,
                badge: accesSecondaires.length,
              },
              { value: 'envois', label: 'Envois', icon: <MdOutgoingMail /> },
              {
                value: 'motifs',
                label: 'Motifs',
                icon: <MdLabel />,
                badge: doctolib.releve?.motifs.length,
              },
            ]}
          />

          <TabPanel id="fiche-utilisateur" value="informations" active={onglet}>
            <div className="flex flex-col gap-8">
              <Section
                icon={<MdBadge />}
                title="Compte et cabinet"
                description="Ce que Fidalia sait du responsable et de son cabinet."
              >
                <div className="grid gap-6 lg:grid-cols-2">
                  <Card className="flex flex-col gap-3">
                    <CardTitle className="text-base">Compte</CardTitle>
                    <dl className="flex flex-col divide-y divide-border-subtle">
                      <Ligne label="Niveau d’accès">
                        {user.principal
                          ? 'Responsable du cabinet'
                          : user.role === 'admin'
                            ? 'Administrateur'
                            : 'Compte secondaire'}
                      </Ligne>
                      <Ligne label="Email vérifié">{user.emailVerifie ? 'Oui' : 'Non'}</Ligne>
                      <Ligne label="Onboarding">
                        {user.onboardingFait ? 'Terminé' : 'En cours'}
                      </Ligne>
                      <Ligne label="Compte créé le">{dateCourte(user.createdAt)}</Ligne>
                    </dl>
                  </Card>

                  <Card className="flex flex-col gap-3">
                    <CardTitle className="text-base">Cabinet</CardTitle>
                    <dl className="flex flex-col divide-y divide-border-subtle">
                      <Ligne label="Exercice">
                        {cabinet.exercice === 'seul' ? 'Praticien seul' : 'Cabinet de groupe'}
                      </Ligne>
                      <Ligne label="Effectif déclaré">
                        {cabinet.effectif ? EFFECTIF_LABELS[cabinet.effectif] : 'Non renseigné'}
                      </Ligne>
                      <Ligne label="Fin d’essai">{dateCourte(cabinet.finEssai)}</Ligne>
                      <Ligne label="Cabinet créé le">{dateCourte(cabinet.createdAt)}</Ligne>
                    </dl>
                  </Card>
                </div>
              </Section>

              <hr className="border-border-subtle" />

              <Section
                icon={<MdSync />}
                title="Doctolib"
                description="Ce que l’extension a relevé lors de la dernière synchronisation."
              >
                <DoctolibTab doctolib={doctolib} />
              </Section>
            </div>
          </TabPanel>

          <TabPanel id="fiche-utilisateur" value="abonnements" active={onglet}>
            <Abonnements cabinet={cabinet} />
          </TabPanel>

          <TabPanel id="fiche-utilisateur" value="acces" active={onglet}>
            <DataTable
              mobileCards
              data={accesSecondaires}
              columns={ACCES_COLUMNS}
              rowId={(membre) => membre.id}
              emptyMessage="Aucun autre accès dans ce cabinet."
            />
          </TabPanel>

          {/* Les envois de ce cabinet : même liste que l'onglet « Envois » du
          back-office, filtrée sur lui. Statut, code du refus, serveur qui a
          porté le message — et aucun contenu, qui appartient au cabinet. */}
          <TabPanel id="fiche-utilisateur" value="envois" active={onglet}>
            <div className="flex flex-col gap-6">
              <ActiviteCabinet cabinetId={cabinet.id} />
              <TableauEnvois cabinetId={cabinet.id} />
            </div>
          </TabPanel>

          <TabPanel id="fiche-utilisateur" value="motifs" active={onglet}>
            <DataTable
              mobileCards
              data={doctolib.releve?.motifs ?? []}
              columns={COLONNES_MOTIFS}
              rowId={(motif) => String(motif.id)}
              emptyMessage="Aucun motif relevé pour ce cabinet."
            />
          </TabPanel>
        </div>

        {/* Fiche d'identité, à gauche : qui, comment le joindre, puis les chiffres qu'on vient
            chercher en premier au support. Sur mobile, elle passe avant les onglets. */}
        <aside className="order-first lg:sticky lg:top-24">
          <Card className="flex flex-col gap-5 p-6">
            <div className="flex items-center gap-4">
              <Avatar name={nom} size="lg" tone="auto" className="size-16 shrink-0 text-base" />
              <div className="flex min-w-0 flex-col gap-1">
                <h1 className="truncate text-xl font-semibold text-content">{nom}</h1>
                <p className="text-sm text-content-muted">{FONCTION_LABELS[user.fonction]}</p>
              </div>
            </div>

            {(!user.principal && user.role === 'admin') || !user.actif ? (
              <div className="flex flex-wrap gap-2">
                {!user.principal && user.role === 'admin' ? (
                  <Badge tone="brand">Administrateur</Badge>
                ) : null}
                {user.actif ? null : <Badge tone="danger">Compte désactivé</Badge>}
              </div>
            ) : null}

            <div className="flex flex-col gap-2.5 text-sm">
              <Contact icon={<MdMail />} label={user.email}>
                {user.emailVerifie ? null : (
                  <Badge tone="neutral" size="sm">
                    non vérifié
                  </Badge>
                )}
              </Contact>
              <Contact icon={<MdPhone />} label={`${user.indicatif} ${user.telephone}`} />
              <Contact icon={<MdBusiness />} label={cabinet.nom} />
              <Contact
                icon={<MdCalendarMonth />}
                label={`Inscrit le ${dateCourte(cabinet.createdAt)}`}
              />
            </div>

            <div className="flex flex-col gap-4 border-t border-border-subtle pt-5">
              <Tuile
                icon={<MdLogin />}
                label="Dernière connexion"
                valeur={<Depuis value={user.lastLoginAt} jamais="Jamais connecté" />}
              />
              <Tuile
                icon={<MdSync />}
                label="Dernière sync. Doctolib"
                valeur={<Depuis value={user.doctolibSyncAt} jamais="Jamais" />}
              />
              <Tuile
                icon={<MdWorkspacePremium />}
                label="Abonnement"
                valeur={(() => {
                  const echu = essaiEchu(cabinet);
                  const icone = iconeAbonnement(cabinet, echu);
                  return cabinet.gold ? (
                    <Badge tone="gold" icon={icone}>
                      Gold
                    </Badge>
                  ) : echu ? (
                    // `statut` reste à `essai` après l'échéance : c'est la date qui
                    // tranche. Afficher « Essai gratuit » suggérerait un accès actif.
                    <Badge tone="neutral" icon={icone}>
                      Inactif
                    </Badge>
                  ) : (
                    <Badge tone={STATUT_TONES[cabinet.statut]} icon={icone}>
                      {STATUT_LABELS[cabinet.statut]}
                    </Badge>
                  );
                })()}
                detail={cabinet.gold ? undefined : detailEssai(joursEssai, cabinet.finEssai)}
              />
            </div>
          </Card>
        </aside>
      </div>
    </div>
  );
}

type Onglet = 'informations' | 'abonnements' | 'acces' | 'envois' | 'motifs';

const COLONNES_TRANSACTIONS = colonnesTransactions({ avecCabinet: false });

/**
 * Historique de facturation du cabinet. Composant à part, donc monté seulement
 * quand l'onglet s'ouvre : la requête traverse Stripe, inutile de la lancer
 * pour quelqu'un qui vient consulter les coordonnées.
 */
function Abonnements({ cabinet }: { cabinet: Cabinet }) {
  const [page, setPage] = useState(1);
  const gold = useSetCabinetGold();
  const [erreurGold, setErreurGold] = useState<string | null>(null);
  const prolonger = useExtendCabinetTrial();
  const essaiProlongeable = cabinet.statut === 'essai' && !cabinet.formule && !cabinet.gold;

  const prolongerEssai = async (jours: number) => {
    setErreurGold(null);
    try {
      await prolonger.mutateAsync({ cabinetId: cabinet.id, jours });
    } catch (cause) {
      setErreurGold(errorMessage(cause, 'Prolongation impossible.'));
    }
  };

  const basculerGold = async (actif: boolean) => {
    setErreurGold(null);
    try {
      await gold.mutateAsync({ cabinetId: cabinet.id, gold: actif });
    } catch (cause) {
      setErreurGold(errorMessage(cause, 'Bascule impossible.'));
    }
  };
  const { data, isPending, error } = useAdminTransactions({
    cabinetId: cabinet.id,
    page,
    limit: 10,
  });

  if (error) {
    return <Alert tone="danger">{errorMessage(error, 'Chargement impossible.')}</Alert>;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Chiffres à gauche, action à droite. Le bouton garde sa place même
          sans transaction : un cabinet fraîchement abonné doit pouvoir
          recevoir un geste commercial avant sa première facture. */}
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-content-muted">
          {data && data.total > 0 ? (
            <p>
              Encaissé depuis l’origine :{' '}
              <span className="font-semibold text-content">{formatEuros(data.encaisse)}</span>
            </p>
          ) : null}

          {data && data.credit !== null ? (
            <p>
              Crédit disponible :{' '}
              <span className="font-semibold text-content">{formatEuros(data.credit)}</span>
              {data.clientUrl ? (
                <>
                  {' · '}
                  <a
                    href={data.clientUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-brand-600 underline underline-offset-2 hover:text-brand-700"
                  >
                    voir sur Stripe
                  </a>
                </>
              ) : null}
            </p>
          ) : null}
        </div>

        {/* Gold ne touche ni à Stripe ni à la formule : c'est un accès
            illimité accordé à part, donc un simple interrupteur. */}
        <Switch
          label="Formule Gold"
          checked={cabinet.gold}
          loading={gold.isPending}
          onChange={(actif) => void basculerGold(actif)}
          title={
            cabinet.gold
              ? 'Retirer l’accès illimité de ce cabinet'
              : 'Accorder un accès illimité à ce cabinet, sans facturation'
          }
        />
      </div>

      {erreurGold ? <Alert tone="danger">{erreurGold}</Alert> : null}

      {essaiProlongeable ? (
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="text-content-muted">
            Essai jusqu’au {dateCourte(cabinet.finEssai)} · Prolonger de
          </span>
          {[7, 14, 30].map((jours) => (
            <Button
              key={jours}
              variant="secondary"
              size="sm"
              loading={prolonger.isPending && prolonger.variables?.jours === jours}
              disabled={prolonger.isPending}
              onClick={() => void prolongerEssai(jours)}
            >
              {jours} jours
            </Button>
          ))}
        </div>
      ) : null}

      {/* Gold ne passe pas par Stripe : aucune facture ne le matérialise, cette
          bannière est la seule trace visible côté back-office. */}
      {cabinet.gold ? (
        <Alert tone="gold" title="Formule Gold active">
          Ce cabinet a un accès illimité, accordé par le back-office. Rien ne lui est facturé.
        </Alert>
      ) : null}

      <DataTable
        mobileCards
        data={data?.items ?? []}
        columns={COLONNES_TRANSACTIONS}
        isLoading={isPending}
        skeletonRows={4}
        rowId={(transaction) => transaction.id}
        emptyMessage="Aucune transaction pour ce cabinet."
      />

      {data && data.total > data.limit ? (
        <Pagination
          page={data.page}
          total={data.total}
          limit={data.limit}
          onChange={setPage}
          label="transactions"
        />
      ) : null}
    </div>
  );
}

const ACCES_COLUMNS: TableColumn<User>[] = [
  {
    key: 'personne',
    title: 'Membre',
    cell: (membre) => {
      const nom = `${membre.prenom} ${membre.nom}`.trim();
      return (
        <div className="flex items-center gap-3">
          <Avatar name={nom} size="sm" tone="auto" />
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium">{nom}</span>
            <span className="truncate text-xs text-content-muted">{membre.email}</span>
          </div>
        </div>
      );
    },
  },
  {
    key: 'fonction',
    title: 'Fonction',
    cell: (membre) => (
      <span className="text-content-muted">{FONCTION_LABELS[membre.fonction]}</span>
    ),
  },
  {
    key: 'acces',
    title: 'Niveau d’accès',
    cell: (membre) =>
      membre.principal ? (
        <Badge tone="accent">Responsable</Badge>
      ) : membre.role === 'admin' ? (
        <Badge tone="brand">Administrateur</Badge>
      ) : (
        <Badge tone="neutral">Compte secondaire</Badge>
      ),
  },
  {
    key: 'statut',
    title: 'Compte',
    align: 'right',
    cell: (membre) =>
      membre.actif ? <Badge tone="success">Actif</Badge> : <Badge tone="danger">Désactivé</Badge>,
  },
];

const COLONNES_MOTIFS: TableColumn<MotifDoctolib>[] = [
  {
    key: 'nom',
    title: 'Motif',
    icon: MdLabel,
    cell: (motif) => (
      <span className="flex min-w-0 items-center gap-2">
        <Pastille couleur={motif.couleur} />
        <span className="truncate font-medium">{motif.nom}</span>
      </span>
    ),
  },
  {
    key: 'duree',
    title: 'Durée',
    cell: (motif) => (
      <span className="text-content-muted">
        {motif.dureeMin !== null ? `${motif.dureeMin} min` : '—'}
      </span>
    ),
  },
  {
    key: 'teleconsultation',
    title: 'Téléconsultation',
    cell: (motif) =>
      motif.teleconsultation ? (
        <Badge tone="success">Oui</Badge>
      ) : (
        <Badge tone="neutral">Non</Badge>
      ),
  },
  {
    key: 'agendas',
    title: 'Agendas',
    align: 'right',
    cell: (motif) => <span className="text-content-muted">{motif.agendaIds.length}</span>,
  },
];

/** Teinte Doctolib d'un motif, en pastille — décorative, sans garantie de contraste. */
function Pastille({ couleur }: { couleur: string | null }) {
  if (!couleur) return <span aria-hidden="true" className="size-2.5 shrink-0" />;
  return (
    <span
      aria-hidden="true"
      style={{ backgroundColor: couleur }}
      className="size-2.5 shrink-0 rounded-full ring-1 ring-inset ring-content/15"
    />
  );
}

/**
 * Le cabinet vu côté Doctolib : sa fiche publique, sa spécialité, le compte
 * qui a synchronisé en dernier. Section de l'onglet « Informations », sous ce
 * que Fidalia sait du compte et du cabinet.
 */
function DoctolibTab({ doctolib }: { doctolib: AdminUserDetail['doctolib'] }) {
  const { releve, specialite, lienDoctolib } = doctolib;

  if (!releve) {
    return (
      <Alert tone="info">
        Ce cabinet n’a jamais synchronisé Doctolib : rien à afficher tant que l’extension n’a pas
        tourné une première fois.
      </Alert>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card className="flex flex-col gap-3">
        <CardTitle className="text-base">Fiche Doctolib</CardTitle>
        <dl className="flex flex-col divide-y divide-border-subtle">
          <Ligne label="Nom du cabinet">{releve.practice?.nom ?? 'Non renseigné'}</Ligne>
          <Ligne label="Adresse">{releve.practice?.adresse ?? 'Non renseigné'}</Ligne>
          <Ligne label="Ville">
            {releve.practice
              ? `${releve.practice.codePostal ?? ''} ${releve.practice.ville}`.trim()
              : 'Non renseigné'}
          </Ligne>
          <Ligne label="Organisation">{releve.organisationNom ?? 'Non renseigné'}</Ligne>
        </dl>
        {lienDoctolib ? (
          <a
            href={lienDoctolib}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 self-start text-sm font-medium text-brand-600 hover:underline"
          >
            <MdPlace aria-hidden="true" className="size-4" />
            Ouvrir la fiche sur Doctolib
            <MdOpenInNew aria-hidden="true" className="size-3.5" />
          </a>
        ) : null}
      </Card>

      <Card className="flex flex-col gap-3">
        <CardTitle className="text-base">Spécialité</CardTitle>
        {specialite ? (
          <dl className="flex flex-col divide-y divide-border-subtle">
            <Ligne label="Nom">
              <span className="inline-flex items-center gap-1.5">
                <MdMedicalServices aria-hidden="true" className="size-4 text-content-subtle" />
                {specialite.nom}
              </span>
            </Ligne>
            <Ligne label="Type">{specialite.genre ?? '—'}</Ligne>
            <Ligne label="Identifiant">{specialite.slug ?? '—'}</Ligne>
            <Ligne label="Groupe">{specialite.groupe ?? '—'}</Ligne>
            <Ligne label="Adaptée aux rappels">{specialite.rappelAdapte ? 'Oui' : 'Non'}</Ligne>
          </dl>
        ) : (
          <p className="text-sm text-content-muted">Non renseignée.</p>
        )}
      </Card>

      <Card className="flex flex-col gap-3 lg:col-span-2">
        <CardTitle className="text-base">Dernière synchronisation</CardTitle>
        <dl className="grid gap-x-6 divide-y divide-border-subtle sm:grid-cols-2 sm:divide-y-0">
          <Ligne label="Compte Doctolib">{releve.compte.nom ?? 'Non renseigné'}</Ligne>
          <Ligne label="Email Doctolib">{releve.compte.email ?? 'Non renseigné'}</Ligne>
          <Ligne label="Synchronisé le">
            {new Date(releve.releveLe).toLocaleString('fr-FR', {
              dateStyle: 'medium',
              timeStyle: 'short',
            })}
          </Ligne>
          <Ligne label="Par">
            {releve.relevePar ? `${releve.relevePar.prenom} ${releve.relevePar.nom}`.trim() : '—'}
          </Ligne>
        </dl>
      </Card>
    </div>
  );
}

function Retour() {
  return (
    <Link to="/users" className="self-start">
      <Button variant="ghost" size="sm" icon={<MdArrowBack className="size-4" />}>
        Retour aux utilisateurs
      </Button>
    </Link>
  );
}

/** Coordonnée du bandeau : icône discrète + valeur. */
function Contact({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children?: ReactNode;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2 text-content-muted">
      <span aria-hidden="true" className="flex shrink-0 [&>svg]:size-4">
        {icon}
      </span>
      <span className="truncate">{label}</span>
      {children}
    </span>
  );
}

/** Une ligne de la fiche d'identité : le libellé à gauche, sa valeur à droite (le détail dessous). */
function Tuile({
  icon,
  label,
  valeur,
  detail,
}: {
  icon: ReactNode;
  label: string;
  valeur: ReactNode;
  detail?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="flex shrink-0 items-center gap-1.5 pt-0.5 text-sm text-content-muted">
        <span aria-hidden="true" className="flex [&>svg]:size-4">
          {icon}
        </span>
        {label}
      </span>
      <div className="flex min-w-0 flex-col items-end gap-0.5 text-right">
        <span className="text-sm font-semibold text-content">{valeur}</span>
        {detail ? <span className="text-xs text-content-muted">{detail}</span> : null}
      </div>
    </div>
  );
}

/** Un bloc de la fiche : son titre et ce qu'il contient, avec de l'air autour. */
function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-content">
          {/* Une icône nue, à la taille et à la couleur du titre : un repère, pas une pastille. */}
          <span aria-hidden="true" className="flex shrink-0 [&>svg]:size-5">
            {icon}
          </span>
          {title}
        </h2>
        <p className="text-sm text-content-muted">{description}</p>
      </div>
      {children}
    </section>
  );
}

/** Libellé à gauche, valeur à droite ; une valeur absente se lit en gris, sans gras. */
function Ligne({ label, children }: { label: string; children: ReactNode }) {
  const vide = children === 'Non renseigné' || children === '—';
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 text-sm">
      <dt className="shrink-0 text-content-muted">{label}</dt>
      <dd
        className={
          vide
            ? 'min-w-0 text-right text-content-subtle'
            : 'min-w-0 text-right font-medium break-words text-content'
        }
      >
        {children}
      </dd>
    </div>
  );
}

/** `null` affiché en tiret plutôt qu'en « Invalid Date ». */
function dateCourte(value: string | null): string {
  if (!value) return 'Non renseigné';
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Sous-titre de la tuile Abonnement : le compte à rebours, puis la date à
 * laquelle il tombe. « 20 jours restants » seul oblige à calculer de tête ;
 * la date seule oblige à compter les jours. Les deux ensemble se lisent.
 */
function detailEssai(jours: number | null, finEssai: string | null): string | undefined {
  if (jours === null) return undefined;

  const date = finEssai ? ` • ${dateNumerique(finEssai)}` : '';
  if (jours === 0) return `Essai terminé${date}`;

  return `${jours} jour${jours > 1 ? 's' : ''} restant${jours > 1 ? 's' : ''}${date}`;
}

/** Format court et non ambigu à côté d'un nombre de jours : 05/10/2026. */
function dateNumerique(value: string): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Jours restants avant la fin d'essai. `null` si aucune échéance. */
function joursRestants(finEssai: string | null): number | null {
  if (!finEssai) return null;
  const ecart = new Date(finEssai).getTime() - Date.now();
  return Math.max(0, Math.ceil(ecart / (24 * 60 * 60 * 1000)));
}

/** Le moment en relatif, la date complète au survol ; `jamais` quand il n'a pas eu lieu. */
function Depuis({ value, jamais }: { value: string | null; jamais: string }) {
  if (!value) return <span className="text-content-muted">{jamais}</span>;
  return (
    <span
      title={new Date(value).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' })}
      className="first-letter:uppercase"
    >
      {depuis(value)}
    </span>
  );
}

/** « il y a 3 jours », pour situer une date sans la calculer de tête. */
function depuis(value: string): string {
  const jours = Math.floor((Date.now() - new Date(value).getTime()) / (24 * 60 * 60 * 1000));
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return 'hier';
  if (jours < 30) return `il y a ${jours} jours`;
  const mois = Math.floor(jours / 30);
  return `il y a ${mois} mois`;
}
